import { autoserialize, autoserializeAs, deserializeAs } from 'cerialize';
import * as moment from 'moment';

import { BaseDonationLead, DonationLeadType, IBaseDonationLead } from './base-donation-lead';
import { CallDetails, ICallDetails } from './call-details';
import { CourierType } from './courier-type';
import { Donation, IDonation } from './donation';
import { DonationMeta, IDonationMeta } from './donation-meta';
import { LeadChannel } from './lead-channel';
import { LeadUTM } from './lead-utm';

export class Lead extends BaseDonationLead implements ILead {
  @autoserializeAs('donation_id') donationId?: string;
  @autoserializeAs('donation') donation?: Donation;

  @autoserializeAs('call_details') callDetails?: CallDetails;

  @autoserializeAs('marketing_source') marketingSource?: string;
  @autoserialize utm?: LeadUTM;
  @autoserialize channel?: LeadChannel;

  @autoserialize meta?: DonationMeta;

  @autoserialize quote_check?: boolean | null;
  @autoserialize abandoned?: string | null;

  /**
   * Read-only: the courier/partner pairs the backend resolved for this lead's address
   * on the last `availability_check`. `/leads/availability` reads them to decide
   * whether the lead gets hourly courier slots, so an empty list on a lead that has
   * an address means the check has not run for it yet.
   *
   * Stripped on serialize — the API rejects it as an update param.
   */
  @autoserializeAs('available_candidates') availableCandidates?: LeadAvailableCandidate[];

  /** Read-only: the cart sits in the Roadie Huge weight band (`Lead#roadie_huge_load`). */
  @deserializeAs('roadie_huge_load') roadieHugeLoad?: boolean;

  /** Read-only, server-computed (`Lead#*_eligible?`): spec, weights, fees, guaranteed items, donor_load. */
  @deserializeAs('uber_eligible') uberEligible?: boolean;
  @deserializeAs('roadie_xlarge_eligible') roadieXlargeEligible?: boolean;
  @deserializeAs('roadie_huge_eligible') roadieHugeEligible?: boolean;

  constructor(payload: ILead) {
    super(payload);

    this.type = DonationLeadType.LEAD;

    this.donation = payload?.donation ? new Donation(payload.donation) : undefined;
    this.donationId = payload?.donationId || payload?.donation?.id;

    this.callDetails = payload?.callDetails ? new CallDetails(payload.callDetails) : undefined;

    this.marketingSource = payload?.marketingSource;
    this.utm = payload?.utm ? new LeadUTM(payload.utm) : undefined;
    this.channel = payload?.channel;

    this.meta = new DonationMeta(payload?.meta);

    this.quote_check = payload?.quote_check;
    this.abandoned = payload?.abandoned;
    this.availableCandidates = payload?.availableCandidates;
    this.roadieHugeLoad = payload?.roadieHugeLoad;
    this.uberEligible = payload?.uberEligible;
    this.roadieXlargeEligible = payload?.roadieXlargeEligible;
    this.roadieHugeEligible = payload?.roadieHugeEligible;

    if (this.specification && this.pricing?.bookingFee && this.total === this.totalSponsoredAmount) {
      this.pricing.bookingFee = 0;
    }
  }

  get isExpired(): boolean {
    return moment().isAfter(moment(this.expiresAt));
  }

  get total(): number {
    return this.originalQuote;
  }

  get fuelFee(): number {
    return this.originalQuoteFuelFee;
  }

  get totalSponsoredAmount(): number {
    return this.originalSponsoredAmount;
  }

  get donatableSmall(): number {
    if (this.discount?.valid) {
      return this.specification.small;
    } else {
      return 0;
    }
  }

  /** Charity has Uber enabled. Falls back to the legacy `courier` boolean for charities
   *  whose `couriers` array hasn't been migrated yet (mirrors backend `Charity#uber_enabled?`). */
  private get charityUberEnabled(): boolean {
    return this.charity?.couriers ? this.charity.couriers.includes(CourierType.UBER) : !!this.charity?.courier;
  }

  private charityOffers(courier: CourierType): boolean {
    return !!this.charity?.couriers?.includes(courier);
  }

  // Eligibility is the server's call (`uber_eligible` etc.) — the client only adds whether the
  // charity on the lead offers that courier, which the flags do not check.
  get isUberEligible(): boolean {
    return this.uberEligible === true && this.charityUberEnabled;
  }

  get isRoadieEligible(): boolean {
    return this.roadieXlargeEligible === true && this.charityOffers(CourierType.ROADIE);
  }

  get isRoadieHugeEligible(): boolean {
    return this.roadieHugeEligible === true && this.charityOffers(CourierType.ROADIE_HUGE);
  }

  get isCourierEligible(): boolean {
    return this.isUberEligible || this.isRoadieEligible || this.isRoadieHugeEligible;
  }

  /** RP-3826: ask the willing-to-load question? Like the donor app's `shouldAskDonorLoad`, except a
   *  charity without Roadie Huge ends it even when answered — the wizard switches charities, the
   *  donor app doesn't. The answer stays on the lead for a switch back. */
  get shouldAskDonorLoad(): boolean {
    if (this.charity && !this.charityOffers(CourierType.ROADIE_HUGE)) return false;
    if (this.donorLoad != null) return true;
    return this.roadieHugeLoad === true;
  }

  get isLead(): boolean {
    return true;
  }

  get isDonation(): boolean {
    return false;
  }
}

export interface LeadAvailableCandidate {
  store_id?: number;
  charity_id?: number;
  distance?: number;
  couriers?: string[];
  partner_ids?: number[];
}

export interface ILead extends IBaseDonationLead {
  donationId?: string;
  donation?: IDonation;

  callDetails?: ICallDetails;

  marketingSource?: string;
  utm?: LeadUTM;
  channel?: LeadChannel;

  meta?: IDonationMeta;

  quote_check?: boolean | null;
  abandoned?: string | null;
  availableCandidates?: LeadAvailableCandidate[];
  roadieHugeLoad?: boolean;
  uberEligible?: boolean;
  roadieXlargeEligible?: boolean;
  roadieHugeEligible?: boolean;
}
